import React, { useEffect, useState } from 'react'
import { Alert, AlertTitle, Box, Typography } from '@mui/material'
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined'
import API from '../../services/API'

// This month's control-room hours, from GET /commandcontrol/controlroom-hours.
//
// Only a plan that enforces its hours (Premium, mode 'hard') ever shows
// anything here:
//   - 10 hours or fewer left: a persistent amber warning;
//   - 5 hours or fewer: a live countdown, ticking down locally between polls
//     at one second per open room per second (every open room uses hours);
//   - none left: open rooms have been (or are about to be) closed by the
//     server, and no room can be started or reopened until next month.
// Enterprise hours are a review threshold and are never shown here.

const POLL_MS = 60 * 1000

const formatClock = (totalSeconds) => {
  const seconds = Math.max(0, Math.floor(totalSeconds))
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = seconds % 60
  return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

const formatHours = (minutes) => {
  const total = Math.max(0, Math.floor(minutes))
  const h = Math.floor(total / 60)
  const m = total % 60
  if (h === 0) return `${m} minute${m === 1 ? '' : 's'}`
  return m === 0 ? `${h} hour${h === 1 ? '' : 's'}` : `${h} h ${m} m`
}

const firstOfNextMonth = () => {
  const now = new Date()
  return new Date(now.getFullYear(), now.getMonth() + 1, 1).toLocaleDateString(
    'en-GB',
    { day: 'numeric', month: 'long' },
  )
}

// { status, remainingSeconds } — status as the server sent it (null until
// loaded, or when it cannot be read), remainingSeconds ticking locally.
export const useControlRoomHours = () => {
  const [status, setStatus] = useState(null)
  const [fetchedAt, setFetchedAt] = useState(0)
  const [now, setNow] = useState(Date.now())

  useEffect(() => {
    let cancelled = false
    const load = () =>
      API.get('/commandcontrol/controlroom-hours', {
        skipGlobalErrorHandler: true,
      })
        .then((res) => {
          if (cancelled || res?.status !== 200) return
          setStatus(res.data)
          setFetchedAt(Date.now())
        })
        .catch(() => {})
    load()
    const poll = setInterval(load, POLL_MS)
    return () => {
      cancelled = true
      clearInterval(poll)
    }
  }, [])

  const counting = status?.mode === 'hard' && status?.countdown
  useEffect(() => {
    if (!counting) return undefined
    const tick = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(tick)
  }, [counting])

  if (!status || typeof status.remainingMinutes !== 'number') {
    return { status, remainingSeconds: null }
  }
  const elapsed = fetchedAt ? Math.max(0, (now - fetchedAt) / 1000) : 0
  const burn = Math.max(0, Number(status.openRooms) || 0)
  return {
    status,
    remainingSeconds: status.remainingMinutes * 60 - elapsed * burn,
  }
}

// `inRoom`: the control-room page (wording about "this room"); otherwise the
// CAD dashboard.
const ControlRoomHoursNotice = ({ inRoom = false, sx }) => {
  const { status, remainingSeconds } = useControlRoomHours()
  if (status?.mode !== 'hard' || remainingSeconds === null) return null
  if (!status.warning && !status.exhausted) return null

  const allowance = formatHours(status.allowanceMinutes ?? 0)

  if (status.exhausted || remainingSeconds <= 0) {
    return (
      <Alert severity="error" sx={{ borderRadius: 3, ...sx }}>
        <AlertTitle sx={{ fontWeight: 800 }}>Control-room hours used</AlertTitle>
        This month's {allowance} of control-room hours have been used. Open
        control rooms are closed automatically and become read-only, and no
        control room can be started or reopened until {firstOfNextMonth()}.
        Contact AmbuNet to add hours or move to Enterprise.
      </Alert>
    )
  }

  if (status.countdown) {
    return (
      <Alert
        severity="error"
        icon={<TimerOutlinedIcon />}
        sx={{ borderRadius: 3, alignItems: 'center', ...sx }}
      >
        <Box
          display="flex"
          flexWrap="wrap"
          alignItems="baseline"
          columnGap={1.5}
          rowGap={0.5}
        >
          <Typography
            component="span"
            fontWeight={900}
            fontSize={22}
            sx={{ fontVariantNumeric: 'tabular-nums' }}
            aria-live="off"
          >
            {formatClock(remainingSeconds)}
          </Typography>
          <Typography component="span" variant="body2">
            of control-room time left this month
            {status.openRooms > 1
              ? ` (${status.openRooms} rooms open, so it runs down ${status.openRooms}× faster)`
              : ''}
            .{' '}
            {inRoom
              ? 'When it reaches zero this control room closes automatically and becomes read-only.'
              : 'When it reaches zero, open control rooms close automatically and become read-only.'}
          </Typography>
        </Box>
      </Alert>
    )
  }

  return (
    <Alert severity="warning" sx={{ borderRadius: 3, ...sx }}>
      <AlertTitle sx={{ fontWeight: 800 }}>
        {formatHours(remainingSeconds / 60)} of control-room hours left this
        month
      </AlertTitle>
      Your plan includes {allowance} a month. When they run out, open control
      rooms close automatically and become read-only, and no control room can
      be started or reopened until {firstOfNextMonth()}. Contact AmbuNet to add
      hours or move to Enterprise.
    </Alert>
  )
}

export default ControlRoomHoursNotice
